package ai.beakman.undercover.game

import ai.beakman.undercover.model.GameResult
import ai.beakman.undercover.model.Theme
import ai.beakman.undercover.model.Vote
import ai.beakman.undercover.model.WordHint
import kotlin.random.Random

/** Resultado do sorteio de uma rodada. */
data class RoundSetup(
    val word: String,
    val hint: String,
    val infiltratorIds: List<String>,
)

/** Lógica pura do jogo — compartilhada por mobile (Compose), web (JS) e modo local. */
object GameLogic {
    // Sem caracteres ambíguos (I, O, 0, 1)
    private const val CODE_CHARS = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789"
    const val MIN_PLAYERS = 3

    fun generateRoomCode(random: Random = Random.Default): String =
        buildString { repeat(5) { append(CODE_CHARS[random.nextInt(CODE_CHARS.length)]) } }

    fun pickWord(theme: Theme, random: Random = Random.Default): WordHint =
        theme.words[random.nextInt(theme.words.size)]

    /** Máximo de infiltrados: precisa sobrar ao menos 2 jogadores comuns. */
    fun maxInfiltrators(playerCount: Int): Int = maxOf(1, playerCount - 2)

    /** Sorteia palavra + infiltrados para uma nova rodada. */
    fun setupRound(
        theme: Theme,
        playerIds: List<String>,
        numInfiltrators: Int,
        random: Random = Random.Default,
    ): RoundSetup {
        val pick = pickWord(theme, random)
        val n = numInfiltrators.coerceIn(1, maxOf(1, playerIds.size - 1))
        val infiltrators = playerIds.shuffled(random).take(n)
        return RoundSetup(pick.word, pick.hint, infiltrators)
    }

    /**
     * Apura os votos e decide o vencedor.
     * Jogadores comuns vencem se os mais votados forem EXATAMENTE o conjunto de infiltrados.
     */
    fun computeResult(
        playerIds: List<String>,
        votes: List<Vote>,
        infiltratorIds: List<String>,
    ): GameResult {
        val tally = playerIds.associateWith { 0 }.toMutableMap()
        votes.forEach { v -> v.votedForIds.forEach { id -> tally[id] = (tally[id] ?: 0) + 1 } }
        val top = tally.values.maxOrNull() ?: 0
        val accused = if (top > 0) tally.filterValues { it == top }.keys.toList() else emptyList()
        val caughtAny = accused.any { it in infiltratorIds }
        val allCaught = infiltratorIds.isNotEmpty() &&
            infiltratorIds.all { it in accused } &&
            accused.size == infiltratorIds.size
        return GameResult(
            tally = tally,
            accusedIds = accused,
            infiltratorIds = infiltratorIds,
            playersWon = allCaught,
            caughtAny = caughtAny,
        )
    }
}
